import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useCart } from '@/hooks/useCart';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { Drawer } from '@/components/ui/Drawer';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { formatINR } from '@/lib/formatters';
import { CartItem } from './CartItem';

export function CartDrawer() {
  const isOpen = useCartStore((s) => s.isDrawerOpen);
  const close = useCartStore((s) => s.closeDrawer);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useCart();

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      void refetch();
    }
  }, [isOpen, isAuthenticated, refetch]);

  const items = data?.items ?? [];
  const isEmpty = items.length === 0;

  const handleCheckout = () => {
    close();
    navigate('/checkout');
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={close}
      side="right"
      ariaLabel="Shopping cart"
      size="w-[min(420px,100vw)]"
    >
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between border-b border-white/[0.06] px-5 py-4">
          <h2 className="font-[family-name:var(--font-editorial)] text-xl text-white">
            Cart
            {data && data.totalItems > 0 && (
              <span className="ml-2 text-sm text-[color:var(--color-text-muted)]">
                ({data.totalItems})
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close cart"
            className="-m-2 rounded-md p-2 text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5">
          {!isAuthenticated ? (
            <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
              <p className="text-[color:var(--color-text-secondary)]">
                Login to view your cart.
              </p>
              <Link
                to="/login?redirectTo=%2Fcart"
                onClick={close}
                className="inline-flex h-11 items-center justify-center rounded-md bg-[color:var(--color-brand-magenta)] px-6 text-sm font-medium text-white"
              >
                Login
              </Link>
            </div>
          ) : isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : isEmpty ? (
            <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
              <p className="text-label-mono text-[color:var(--color-text-muted)]">
                Your cart is empty
              </p>
              <Link
                to="/products"
                onClick={close}
                className="inline-flex h-11 items-center justify-center rounded-md border border-[color:var(--color-brand-magenta)] px-6 text-sm font-medium text-[color:var(--color-brand-magenta-light)] transition-colors hover:bg-[color:var(--color-brand-magenta)]/10"
              >
                Continue Shopping
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col">
              <AnimatePresence initial={false}>
                {items.map((item) => (
                  <CartItem key={item.id} item={item} variant="compact" />
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>

        {isAuthenticated && data && !isEmpty && (
          <footer className="border-t border-white/[0.06] px-5 py-5">
            <div className="mb-3 flex items-baseline justify-between">
              <span className="text-label-mono text-[color:var(--color-text-muted)]">
                Subtotal
              </span>
              <span className="font-[family-name:var(--font-display)] text-xl text-white">
                {formatINR(data.subtotal)}
              </span>
            </div>
            <p className="mb-4 text-xs text-[color:var(--color-text-muted)]">
              Shipping & GST calculated at checkout
            </p>
            <Button block onClick={handleCheckout}>
              Checkout
            </Button>
          </footer>
        )}
      </div>
    </Drawer>
  );
}
